import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import {
  IonAlert, IonButton, IonCheckbox, IonContent, IonHeader, IonInput, IonItem, IonLabel,
  IonList, IonModal, IonNote, IonPage, IonSelect, IonSelectOption, IonTitle, IonToolbar,
} from '@ionic/react';
import { computeEventQuoteGuestScenarios, type EventQuote, type EventQuoteGuestScenarios } from '@trackeat/shared';
import { eventMenuCost } from './eventQuoteMenu.js';
import './EventQuoteSimulator.css';

export interface EventQuoteRecipeOption {
  id: string;
  name: string;
  costPerPortion: number;
}

interface EventQuoteSimulatorProps {
  presentation?: 'modal' | 'page';
  isOpen: boolean;
  onClose: () => void;
  recipes: readonly EventQuoteRecipeOption[];
  defaultVatRate: number;
  destinations?: readonly { id: string; name: string }[];
  initialDraft?: EventQuoteSaveDraft | null;
  onSave?: (draft: EventQuoteSaveDraft) => Promise<void>;
  resolveRecipeCost?: (recipeId: string, window: { seasonStart: string; seasonEnd: string; asOf: string }) => EventQuoteResolvedCost;
}

export interface EventQuoteResolvedCost {
  costPerPortion: number | null;
  source: 'seasonal_history' | 'current_cost' | 'unavailable';
  includedObservations: number;
  latestDocumentDate: string | null;
}

export interface EventQuoteSaveDraft {
  eventName: string;
  customerName: string | null;
  eventDate: string;
  seasonStart: string;
  seasonEnd: string;
  asOf: string;
  guestsMin: number;
  guestsExpected: number;
  guestsMax: number;
  selectedRecipeIds: string[];
  portionsByRecipe?: Record<string, number>;
  logisticsMode: 'onsite' | 'internal_transfer' | 'external_delivery';
  destinationId: string | null;
  manualMenuCost: number | null;
  consumablesPerGuest: number;
  otherVariablePerGuest: number;
  minStaff: number;
  guestsPerWorker: number;
  staffHours: number;
  loadedHourlyRate: number;
  eventCosts: number;
  structureShare: number;
  grossPricePerGuest: number;
  guestVat: number;
  grossRoomRevenue: number;
  roomVat: number;
  targetProfit: number;
  forecastAdjustmentPct: number;
  costBasis: Array<{ recipeId: string } & EventQuoteResolvedCost>;
  scenarios: EventQuoteGuestScenarios;
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'number' | 'date' | 'text';
  step?: string;
};

const card: CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14, marginBottom: 12,
};
const grid: CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12,
};
const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

function Field({ label, value, onChange, type = 'number', step }: FieldProps): JSX.Element {
  return (
    <IonInput
      label={label}
      aria-label={label}
      labelPlacement="stacked"
      fill="outline"
      type={type}
      inputmode={type === 'number' ? 'decimal' : undefined}
      step={step}
      value={value}
      onIonInput={(event) => onChange(String(event.detail.value ?? ''))}
    />
  );
}

function numberOrNull(raw: string): number | null {
  if (raw.trim() === '') return null;
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function positiveIntegerOrNull(raw: string): number | null {
  const value = numberOrNull(raw);
  return value != null && Number.isInteger(value) && value > 0 ? value : null;
}

function money(value: number | null): string {
  return value == null ? '—' : eur.format(value);
}

function pct(value: number | null): string {
  return value == null ? '—' : `${value.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function ScenarioTable({ scenarios }: { scenarios: EventQuoteGuestScenarios }): JSX.Element {
  const rows: Array<{ label: string; quote: EventQuote }> = [
    { label: 'Minimi', quote: scenarios.min },
    { label: 'Previsti', quote: scenarios.expected },
    { label: 'Massimi', quote: scenarios.max },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: '#64748b', textAlign: 'right' }}>
            <th style={{ textAlign: 'left', padding: 7 }}>Scenario</th>
            <th>Coperti</th><th>Costo totale</th><th>Costo/coperto</th><th>Ricavo netto</th>
            <th>Risultato</th><th>Margine</th><th>Prezzo obiettivo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, quote }) => (
            <tr key={label} style={{ borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <td style={{ textAlign: 'left', padding: 8, fontWeight: 700 }}>{label}</td>
              <td>{quote.guests}</td>
              <td>{money(quote.totalCost)}</td><td>{money(quote.costPerGuest)}</td><td>{money(quote.netRevenue)}</td>
              <td style={{ color: (quote.operatingResult ?? 0) < 0 ? '#b91c1c' : '#15803d', fontWeight: 700 }}>{money(quote.operatingResult)}</td>
              <td>{pct(quote.marginPct)}</td><td>{money(quote.targetGrossPricePerGuest)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EventQuoteSimulator({
  isOpen, onClose, recipes, defaultVatRate, destinations = [], initialDraft = null, onSave, resolveRecipeCost, presentation = 'modal',
}: EventQuoteSimulatorProps): JSX.Element {
  const page = presentation === 'page';
  const [step, setStep] = useState(0);
  const [dirty, setDirty] = useState(false);
  const history = useHistory();
  const [exitPath, setExitPath] = useState<string | null>(null);
  useEffect(() => {
    if (!dirty && exitPath) {
      setExitPath(null);
      history.push(exitPath);
    }
  }, [dirty, exitPath, history]);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const steps = ['Evento e ospiti', 'Menu e porzioni', 'Costi e prezzo', 'Riepilogo'];
  const [recipeSearch, setRecipeSearch] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const year = today.slice(0, 4);
  const [eventName, setEventName] = useState(onSave ? '' : 'Evento prova');
  const [customerName, setCustomerName] = useState('');
  const [eventDate, setEventDate] = useState(today);
  const [seasonStart, setSeasonStart] = useState(`${year}-01-01`);
  const [seasonEnd, setSeasonEnd] = useState(`${year}-12-31`);
  const [asOf, setAsOf] = useState(today);
  const [guestsMin, setGuestsMin] = useState('80');
  const [guestsExpected, setGuestsExpected] = useState('100');
  const [guestsMax, setGuestsMax] = useState('120');
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([]);
  const [portionsByRecipe, setPortionsByRecipe] = useState<Record<string, string>>({});
  const [manualMenuCost, setManualMenuCost] = useState(onSave ? '' : '30');
  const [manualCostConfirmed, setManualCostConfirmed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [consumablesPerGuest, setConsumablesPerGuest] = useState(onSave ? '' : '3');
  const [otherVariablePerGuest, setOtherVariablePerGuest] = useState('0');
  const [minStaff, setMinStaff] = useState(onSave ? '' : '4');
  const [guestsPerWorker, setGuestsPerWorker] = useState('25');
  const [staffHours, setStaffHours] = useState(onSave ? '' : '8');
  const [loadedHourlyRate, setLoadedHourlyRate] = useState(onSave ? '' : '24');
  const [eventCosts, setEventCosts] = useState(onSave ? '' : '500');
  const [structureShare, setStructureShare] = useState(onSave ? '' : '800');
  const [grossPricePerGuest, setGrossPricePerGuest] = useState(onSave ? '' : '100');
  const [guestVat, setGuestVat] = useState(String(defaultVatRate));
  const [guestVatTouched, setGuestVatTouched] = useState(false);
  const [grossRoomRevenue, setGrossRoomRevenue] = useState('0');
  const [roomVat, setRoomVat] = useState('22');
  const [targetProfit, setTargetProfit] = useState(onSave ? '' : '2000');
  const [forecastAdjustmentPct, setForecastAdjustmentPct] = useState(onSave ? '0' : '8');
  const [logisticsMode, setLogisticsMode] = useState<EventQuoteSaveDraft['logisticsMode']>('onsite');
  const [destinationId, setDestinationId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    const changed = () => setDirty(true);
    content?.addEventListener('ionInput', changed);
    content?.addEventListener('ionChange', changed);
    return () => {
      content?.removeEventListener('ionInput', changed);
      content?.removeEventListener('ionChange', changed);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!page || !dirty || saving) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [page, dirty, saving]);

  useEffect(() => {
    if (!isOpen) setGuestVatTouched(false);
  }, [isOpen]);

  useEffect(() => {
    if (!guestVatTouched) setGuestVat(String(defaultVatRate));
  }, [defaultVatRate, guestVatTouched]);

  useEffect(() => {
    if (!isOpen || !initialDraft) return;
    setManualCostConfirmed(false); setSaveError(null);
    setEventName(initialDraft.eventName); setCustomerName(initialDraft.customerName ?? '');
    setEventDate(initialDraft.eventDate); setSeasonStart(initialDraft.seasonStart); setSeasonEnd(initialDraft.seasonEnd); setAsOf(initialDraft.asOf);
    setGuestsMin(String(initialDraft.guestsMin)); setGuestsExpected(String(initialDraft.guestsExpected)); setGuestsMax(String(initialDraft.guestsMax));
    setSelectedRecipeIds([...initialDraft.selectedRecipeIds]); setLogisticsMode(initialDraft.logisticsMode); setDestinationId(initialDraft.destinationId ?? '');
    setPortionsByRecipe(Object.fromEntries(Object.entries(initialDraft.portionsByRecipe ?? {}).map(([id, value]) => [id, String(value)])));
    setManualMenuCost(initialDraft.manualMenuCost == null ? '' : String(initialDraft.manualMenuCost));
    setConsumablesPerGuest(String(initialDraft.consumablesPerGuest)); setOtherVariablePerGuest(String(initialDraft.otherVariablePerGuest));
    setMinStaff(String(initialDraft.minStaff)); setGuestsPerWorker(String(initialDraft.guestsPerWorker)); setStaffHours(String(initialDraft.staffHours));
    setLoadedHourlyRate(String(initialDraft.loadedHourlyRate)); setEventCosts(String(initialDraft.eventCosts)); setStructureShare(String(initialDraft.structureShare));
    setGrossPricePerGuest(String(initialDraft.grossPricePerGuest)); setGuestVat(String(initialDraft.guestVat)); setGuestVatTouched(true);
    setGrossRoomRevenue(String(initialDraft.grossRoomRevenue)); setRoomVat(String(initialDraft.roomVat)); setTargetProfit(String(initialDraft.targetProfit));
    setForecastAdjustmentPct(String(initialDraft.forecastAdjustmentPct ?? 0));
  }, [initialDraft, isOpen]);

  const selectedRecipes = useMemo(
    () => recipes.filter((recipe) => selectedRecipeIds.includes(recipe.id)),
    [recipes, selectedRecipeIds],
  );
  const resolvedCosts = useMemo(() => selectedRecipes.map((recipe) => {
    const resolved = resolveRecipeCost?.(recipe.id, { seasonStart, seasonEnd, asOf }) ?? {
      costPerPortion: recipe.costPerPortion, source: 'current_cost' as const,
      includedObservations: 0, latestDocumentDate: null,
    };
    const adjustment = numberOrNull(forecastAdjustmentPct) ?? 0;
    return { recipeId: recipe.id, ...resolved, costPerPortion: resolved.costPerPortion == null ? null : resolved.costPerPortion * (1 + adjustment / 100) };
  }), [asOf, forecastAdjustmentPct, resolveRecipeCost, seasonEnd, seasonStart, selectedRecipes]);
  const invalidPortions = selectedRecipeIds.some((id) => {
    const dose = numberOrNull(portionsByRecipe[id] ?? '1');
    return dose == null || dose <= 0;
  });
  const recipeMenuCost = eventMenuCost(selectedRecipeIds, resolvedCosts, portionsByRecipe);
  const seasonalResolvedCount = resolvedCosts.filter((item) => item.source === 'seasonal_history').length;

  const calculated = useMemo((): { scenarios: EventQuoteGuestScenarios | null; error: string | null } => {
    if (!eventDate || !seasonStart || !seasonEnd || !asOf || seasonStart > seasonEnd) {
      return { scenarios: null, error: 'Controlla data evento, periodo della stagione e data di calcolo.' };
    }
    const min = positiveIntegerOrNull(guestsMin);
    const expected = positiveIntegerOrNull(guestsExpected);
    const max = positiveIntegerOrNull(guestsMax);
    const price = numberOrNull(grossPricePerGuest);
    const vat = numberOrNull(guestVat);
    const room = numberOrNull(grossRoomRevenue);
    const roomVatValue = numberOrNull(roomVat);
    const consumables = numberOrNull(consumablesPerGuest);
    const otherVariable = numberOrNull(otherVariablePerGuest);
    const staffMin = numberOrNull(minStaff);
    const ratio = numberOrNull(guestsPerWorker);
    const hours = numberOrNull(staffHours);
    const hourlyRate = numberOrNull(loadedHourlyRate);
    const eventAmount = numberOrNull(eventCosts);
    const structureAmount = numberOrNull(structureShare);
    const profit = numberOrNull(targetProfit);
    if ([min, expected, max, price, vat, room, roomVatValue, consumables, otherVariable, staffMin, ratio, hours, hourlyRate, eventAmount, structureAmount, profit].some((v) => v == null)) {
      return { scenarios: null, error: 'Completa i campi numerici con valori maggiori o uguali a zero.' };
    }
    if (!Number.isInteger(staffMin) || min! > expected! || expected! > max!) {
      return { scenarios: null, error: 'Controlla coperti minimi/previsti/massimi e numero minimo di addetti.' };
    }
    const menuCost = recipeMenuCost ?? (!onSave || manualCostConfirmed ? numberOrNull(manualMenuCost) : null);
    try {
      return {
        scenarios: computeEventQuoteGuestScenarios({
          grossPricePerGuest: price!, guestVatRatePct: vat!, grossRoomRevenue: room!, roomVatRatePct: roomVatValue!,
          targetProfit: profit!, menuLines: [{ id: 'menu', costPerGuest: menuCost }],
          variableLines: [
            { id: 'consumabili', costPerGuest: consumables! },
            { id: 'altri-variabili', costPerGuest: otherVariable! },
          ],
          eventLines: [{ id: 'costi-evento', amount: eventAmount! }],
          structureLines: [{ id: 'quota-struttura', amount: structureAmount! }],
          laborLines: [{
            id: 'personale-servizio', hours: hours!, loadedHourlyRate: hourlyRate!, minHeadcount: staffMin!,
            ...(ratio! > 0 ? { guestsPerWorker: ratio! } : {}),
          }],
          maxBreakEvenGuests: Math.max(500, max! * 4),
        }, { min: min!, expected: expected!, max: max! }),
        error: null,
      };
    } catch {
      return { scenarios: null, error: 'Uno o più valori non sono compatibili con il calcolo.' };
    }
  }, [
    guestsMin, guestsExpected, guestsMax, grossPricePerGuest, guestVat, grossRoomRevenue, roomVat,
    consumablesPerGuest, otherVariablePerGuest, minStaff, guestsPerWorker, staffHours, loadedHourlyRate,
    eventCosts, structureShare, targetProfit, recipeMenuCost, manualMenuCost,
    eventDate, seasonStart, seasonEnd, asOf, onSave, manualCostConfirmed,
  ]);

  const expected = calculated.scenarios?.expected ?? null;
  const missingCostFields = [
    ['consumabili', consumablesPerGuest], ['altri costi per ospite', otherVariablePerGuest],
    ['addetti minimi', minStaff], ['ospiti per addetto', guestsPerWorker], ['ore per addetto', staffHours],
    ['costo orario', loadedHourlyRate], ['costi evento', eventCosts], ['quota struttura', structureShare],
    ['prezzo per ospite', grossPricePerGuest], ['IVA menu', guestVat], ['corrispettivo sala', grossRoomRevenue],
    ['IVA sala', roomVat], ['risultato obiettivo', targetProfit],
  ].filter(([, value]) => numberOrNull(value) == null).map(([label]) => label);
  const canSave = !!expected && expected.status !== 'incomplete' && !saving && !invalidPortions
    && !!eventName.trim() && numberOrNull(forecastAdjustmentPct) != null
    && (recipeMenuCost != null || numberOrNull(manualMenuCost) != null)
    && (logisticsMode === 'onsite' || !!destinationId);
  const saveLabel = saving ? 'Salvataggio…' : initialDraft ? 'Salva nuova revisione' : 'Salva preventivo ed evento';
  function goToStep(next: number): void {
    setStep(next);
    void contentRef.current?.scrollToTop(0);
  }
  const toggleRecipe = (id: string, checked: boolean) => setSelectedRecipeIds((current) => (
    checked ? [...new Set([...current, id])] : current.filter((value) => value !== id)
  ));

  async function save(): Promise<void> {
    if (!onSave || !calculated.scenarios || calculated.scenarios.expected.status === 'incomplete' || invalidPortions) return;
    const values = {
      guestsMin: positiveIntegerOrNull(guestsMin), guestsExpected: positiveIntegerOrNull(guestsExpected), guestsMax: positiveIntegerOrNull(guestsMax),
      manualMenuCost: numberOrNull(manualMenuCost), consumablesPerGuest: numberOrNull(consumablesPerGuest), otherVariablePerGuest: numberOrNull(otherVariablePerGuest),
      minStaff: numberOrNull(minStaff), guestsPerWorker: numberOrNull(guestsPerWorker), staffHours: numberOrNull(staffHours), loadedHourlyRate: numberOrNull(loadedHourlyRate),
      eventCosts: numberOrNull(eventCosts), structureShare: numberOrNull(structureShare), grossPricePerGuest: numberOrNull(grossPricePerGuest), guestVat: numberOrNull(guestVat),
      grossRoomRevenue: numberOrNull(grossRoomRevenue), roomVat: numberOrNull(roomVat), targetProfit: numberOrNull(targetProfit),
      forecastAdjustmentPct: numberOrNull(forecastAdjustmentPct),
    };
    const requiredValues = { ...values, manualMenuCost: 0 };
    if (!eventName.trim() || Object.values(requiredValues).some((value) => value == null) || (recipeMenuCost == null && values.manualMenuCost == null) || (logisticsMode !== 'onsite' && !destinationId)) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        eventName: eventName.trim(), customerName: customerName.trim() || null, eventDate, seasonStart, seasonEnd, asOf,
        guestsMin: values.guestsMin!, guestsExpected: values.guestsExpected!, guestsMax: values.guestsMax!, selectedRecipeIds: [...selectedRecipeIds],
        portionsByRecipe: Object.fromEntries(selectedRecipeIds.map((id) => [id, Number((portionsByRecipe[id] ?? '1').replace(',', '.'))])),
        logisticsMode, destinationId: destinationId || null, manualMenuCost: values.manualMenuCost,
        consumablesPerGuest: values.consumablesPerGuest!, otherVariablePerGuest: values.otherVariablePerGuest!, minStaff: values.minStaff!,
        guestsPerWorker: values.guestsPerWorker!, staffHours: values.staffHours!, loadedHourlyRate: values.loadedHourlyRate!, eventCosts: values.eventCosts!,
        structureShare: values.structureShare!, grossPricePerGuest: values.grossPricePerGuest!, guestVat: values.guestVat!, grossRoomRevenue: values.grossRoomRevenue!,
        roomVat: values.roomVat!, targetProfit: values.targetProfit!, scenarios: calculated.scenarios,
        forecastAdjustmentPct: values.forecastAdjustmentPct!,
        costBasis: resolvedCosts,
      });
      onClose();
    } catch { setSaveError('Il preventivo non è stato salvato. Controlla la sede attiva e riprova: i dati inseriti restano disponibili.'); }
    finally { setSaving(false); }
  }

  const body = (
    <>
      {page && <Prompt when={dirty && !saving} message={(location) => { setExitPath(`${location.pathname}${location.search}${location.hash}`); return false; }} />}
      {page && <IonAlert isOpen={dirty && !!exitPath} header="Uscire senza salvare?" message="Le modifiche al preventivo non sono state salvate." buttons={[{ text: 'Continua a compilare', role: 'cancel', handler: () => setExitPath(null) }, { text: 'Esci senza salvare', handler: () => setDirty(false) }]} onDidDismiss={() => setExitPath(null)} />}
      <IonHeader>
        <IonToolbar>
          <IonTitle>{page ? initialDraft ? 'Modifica preventivo' : 'Nuovo preventivo' : 'Studio preventivo evento'}</IonTitle>
          <IonButton slot="end" fill="clear" disabled={saving} onClick={onClose}>{page ? 'Torna agli eventi' : 'Chiudi'}</IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef} className={page ? 'te-quote-page' : undefined} style={{ '--background': 'var(--te-canvas, #f8fafc)' }}>
        <div className={page ? 'te-quote-layout' : undefined} style={page ? undefined : { maxWidth: 920, margin: '0 auto', padding: 14 }}>
          {page && <nav className="te-quote-steps" aria-label="Sezioni del preventivo"><p>COMPILA IL PREVENTIVO</p>{steps.map((label, index) => <button key={label} type="button" aria-current={step === index ? 'step' : undefined} onClick={() => goToStep(index)}><span>{index + 1}</span>{label}</button>)}<small>Puoi passare da una sezione all’altra senza perdere i dati. Salva al termine.</small></nav>}
          <div className={page ? 'te-quote-form' : undefined}>
          {page && <header className="te-quote-intro"><span>Passaggio {step + 1} di 4</span><h1>{steps[step]}</h1><p>{['Chi stai ospitando, quando e con quanti invitati.', 'Scegli i piatti e le porzioni da prevedere per ogni ospite.', 'Inserisci le spese della struttura e il prezzo che vuoi proporre.', 'Controlla gli scenari economici prima di salvare il preventivo.'][step]}</p></header>}
          <div style={{ ...card, background: '#fff7ed', borderColor: '#fdba74', color: '#9a3412' }}>
            <b>{onSave ? 'PREVENTIVO REVISIONABILE' : 'SIMULAZIONE — NON GESTIONALE'}</b>
            <div style={{ fontSize: 12.5, marginTop: 3 }}>
              {onSave ? 'Compila i costi della tua struttura: i campi vuoti non valgono zero. Il salvataggio conserva una revisione del preventivo; non scarica merce. Prima della produzione verifica nuovamente coperti e ricette.' : 'Non viene salvata e i valori iniziali sono dimostrativi.'}
            </div>
          </div>

          <section hidden={page && step !== 0} style={card}>
            <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{page ? 'Dati dell’evento' : '1. Evento, periodo costi e coperti'}</h2>
            <div style={grid}>
              <Field label="Nome evento" type="text" value={eventName} onChange={setEventName} />
              <Field label="Cliente" type="text" value={customerName} onChange={setCustomerName} />
              <Field label="Data evento" type="date" value={eventDate} onChange={setEventDate} />
              <Field label="Coperti minimi" value={guestsMin} onChange={setGuestsMin} step="1" />
              <Field label="Coperti previsti" value={guestsExpected} onChange={setGuestsExpected} step="1" />
              <Field label="Coperti massimi" value={guestsMax} onChange={setGuestsMax} step="1" />
              <IonSelect label="Logistica" labelPlacement="stacked" fill="outline" value={logisticsMode} onIonChange={(event) => setLogisticsMode(event.detail.value as EventQuoteSaveDraft['logisticsMode'])}>
                <IonSelectOption value="onsite">Produzione e servizio in sede</IonSelectOption>
                <IonSelectOption value="internal_transfer">Trasferimento verso propria sala</IonSelectOption>
                <IonSelectOption value="external_delivery">DDT verso location esterna</IonSelectOption>
              </IonSelect>
              {logisticsMode !== 'onsite' && (
                <IonSelect label="Sala / destinazione" labelPlacement="stacked" fill="outline" value={destinationId} onIonChange={(event) => setDestinationId(String(event.detail.value ?? ''))}>
                  {destinations.map((destination) => <IonSelectOption key={destination.id} value={destination.id}>{destination.name}</IonSelectOption>)}
                </IonSelect>
              )}
            </div>
            <details className="te-quote-history" open={!page}><summary>Periodo storico e adeguamento dei prezzi</summary><div style={grid}>
              <Field label="Stagione dal" type="date" value={seasonStart} onChange={setSeasonStart} />
              <Field label="Stagione al" type="date" value={seasonEnd} onChange={setSeasonEnd} />
              <Field label="Calcolo alla data" type="date" value={asOf} onChange={setAsOf} />
              <Field label="Adeguamento prudenziale prezzi %" value={forecastAdjustmentPct} onChange={setForecastAdjustmentPct} />
            </div><p style={{ margin: '9px 0 0', fontSize: 14, color: '#64748b' }}>
              Il periodo filtra i prezzi storici validi dei DDT/fatture e usa la media ponderata per quantità. La data di calcolo impedisce di usare informazioni conosciute successivamente.
              {' '}Per un evento futuro puoi confrontare lo stesso periodo dell’anno precedente. L’adeguamento percentuale è una tua ipotesi prudenziale, non una previsione automatica del mercato.
            </p></details>
          </section>

          <section hidden={page && step !== 1} style={card}>
            <h2 style={{ fontSize: 16, margin: '0 0 6px' }}>{page ? 'Componi il menu' : '2. Menu e costi per ospite'}</h2>
            {recipes.length > 0 ? (
              <>
                <IonNote>Seleziona i piatti e indica quante porzioni prevedi per ospite: 1 per tutti, 0,5 per metà degli invitati o mezza porzione da buffet. Lo stesso coefficiente guida sia il costo sia il fabbisogno.</IonNote>
                {page && <Field label="Cerca un piatto" type="text" value={recipeSearch} onChange={setRecipeSearch} />}
                <IonList style={page ? { margin: '12px 0' } : { maxHeight: 210, overflowY: 'auto', margin: '8px 0' }}>
                  {recipes.filter((recipe) => !page || recipe.name.toLocaleLowerCase('it-IT').includes(recipeSearch.trim().toLocaleLowerCase('it-IT'))).map((recipe) => (
                    <IonItem key={recipe.id} lines="full">
                      <IonCheckbox slot="start" aria-label={`Includi ${recipe.name} nel menu`} checked={selectedRecipeIds.includes(recipe.id)} onIonChange={(e) => toggleRecipe(recipe.id, e.detail.checked)} />
                      <IonLabel className="ion-text-wrap">{recipe.name}{selectedRecipeIds.includes(recipe.id) && <Field label="Porzioni per ospite" value={portionsByRecipe[recipe.id] ?? '1'} onChange={(value) => setPortionsByRecipe((current) => ({ ...current, [recipe.id]: value }))} />}</IonLabel>
                      <IonNote slot="end">{eur.format(recipe.costPerPortion)}</IonNote>
                    </IonItem>
                  ))}
                </IonList>
              </>
            ) : (
              <p style={{ fontSize: 13, color: '#b45309' }}>Nessuna ricetta con costo completo disponibile. Puoi stimare il preventivo manualmente; per acquisti e produzione dovrai prima configurare il menu con ingredienti, dosi e rese.</p>
            )}
            <div style={grid}>
              <Field label="Costo menu manuale €/ospite" value={manualMenuCost} onChange={setManualMenuCost} />
              <Field label="Consumabili €/ospite" value={consumablesPerGuest} onChange={setConsumablesPerGuest} />
              <Field label="Altri variabili €/ospite" value={otherVariablePerGuest} onChange={setOtherVariablePerGuest} />
            </div>
            {invalidPortions && <p role="alert">Le porzioni per ospite devono essere maggiori di zero.</p>}
            {selectedRecipeIds.some((id) => !recipes.some((recipe) => recipe.id === id)) && <p role="alert">Alcuni piatti della revisione non hanno più un costo completo. Completa le ricette prima di modificare il preventivo.</p>}
            {onSave && recipeMenuCost == null && <IonItem lines="none"><IonCheckbox checked={manualCostConfirmed} onIonChange={(change) => setManualCostConfirmed(change.detail.checked)}><IonLabel className="ion-text-wrap">Confermo l’uso del costo menu manuale come stima, non come costo verificato delle ricette.</IonLabel></IonCheckbox></IonItem>}
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
              Base menu usata: <b>{recipeMenuCost != null ? `${selectedRecipes.length} ricette · ${eur.format(recipeMenuCost)}/ospite` : onSave && !manualCostConfirmed ? 'da confermare: stima manuale' : `${manualMenuCost || '—'} €/ospite (stima manuale)`}</b>.
            </p>
            {resolveRecipeCost && selectedRecipes.length > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: seasonalResolvedCount === selectedRecipes.length ? '#15803d' : '#b45309' }}>
                Storico stagionale disponibile per {seasonalResolvedCount}/{selectedRecipes.length} ricette. Le ricette senza una base completa non producono un costo parziale: usa il costo manuale e completa lo storico.
              </p>
            )}
          </section>

          <section hidden={page && step !== 2} style={card}>
            <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{page ? 'Personale, evento e struttura' : '3. Personale, evento e struttura'}</h2>
            <div style={grid}>
              <Field label="Addetti minimi" value={minStaff} onChange={setMinStaff} step="1" />
              <Field label="Un addetto ogni N ospiti" value={guestsPerWorker} onChange={setGuestsPerWorker} step="1" />
              <Field label="Ore per addetto" value={staffHours} onChange={setStaffHours} />
              <Field label="Costo aziendale €/ora" value={loadedHourlyRate} onChange={setLoadedHourlyRate} />
              <Field label="Altri costi evento €" value={eventCosts} onChange={setEventCosts} />
              <Field label="Quota struttura €" value={structureShare} onChange={setStructureShare} />
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
              Gli addetti crescono a scaglioni: il motore usa il maggiore fra minimo e arrotondamento per eccesso dei coperti/rapporto.
              {' '}Includi preparazione, servizio e riassetto nelle ore; usa il costo aziendale, non il solo compenso netto. Nei costi evento inserisci ad esempio trasporto e noleggi; nella quota struttura la parte di costi generali attribuita a questo evento, senza duplicare il personale. Inserisci 0 soltanto quando il costo è effettivamente assente.
            </p>
          </section>

          <section hidden={page && step !== 2} style={card}>
            <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{page ? 'Ricavi e obiettivo' : '4. Ricavi e obiettivo'}</h2>
            <div style={grid}>
              <Field label="Prezzo lordo €/ospite" value={grossPricePerGuest} onChange={setGrossPricePerGuest} />
              <Field label="IVA menu %" value={guestVat} onChange={(value) => { setGuestVatTouched(true); setGuestVat(value); }} />
              <Field label="Corrispettivo sala lordo €" value={grossRoomRevenue} onChange={setGrossRoomRevenue} />
              <Field label="IVA sala %" value={roomVat} onChange={setRoomVat} />
              <Field label="Risultato obiettivo €" value={targetProfit} onChange={setTargetProfit} />
            </div>
          </section>

          <section hidden={page && step !== 3} style={{ ...card, borderColor: '#93c5fd' }}>
            <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{page ? 'Risultato' : '5. Risultato'} · {eventName.trim() || 'Evento prova'}</h2>
            {calculated.error && <p style={{ color: '#b91c1c' }}>{calculated.error}</p>}
            {calculated.scenarios && expected && (
              <>
                {expected.status === 'incomplete' && (
                  <div style={{ color: '#b45309', background: '#fffbeb', padding: 9, borderRadius: 8, marginBottom: 10 }}>
                    Calcolo incompleto: manca {expected.missingLineIds.join(', ')}. Nessun totale viene inventato come zero.
                  </div>
                )}
                <div style={{ ...grid, marginBottom: 12 }}>
                  <div><small>Costo previsto</small><div style={{ fontSize: 19, fontWeight: 800 }}>{money(expected.totalCost)}</div></div>
                  <div><small>Margine di contribuzione</small><div style={{ fontSize: 19, fontWeight: 800 }}>{money(expected.contribution)}</div></div>
                  <div><small>Risultato dopo struttura</small><div style={{ fontSize: 19, fontWeight: 800, color: (expected.operatingResult ?? 0) < 0 ? '#b91c1c' : '#15803d' }}>{money(expected.operatingResult)}</div></div>
                  <div><small>Pareggio stabile nel range simulato</small><div style={{ fontSize: 19, fontWeight: 800 }}>{expected.breakEvenGuests == null ? '—' : `${expected.breakEvenGuests} ospiti`}</div></div>
                </div>
                <ScenarioTable scenarios={calculated.scenarios} />
                <div style={{ marginTop: 12, padding: 10, background: '#eff6ff', borderRadius: 10, fontSize: 12.5, color: '#1e3a8a' }}>
                  Il prezzo obiettivo comprende tutti i costi inseriti, la quota struttura e il risultato desiderato, al netto dell'eventuale corrispettivo sala. IVA menu e sala restano separate.
                </div>
                {onSave && !page && (
                  <IonButton expand="block" style={{ marginTop: 14 }} disabled={!canSave} onClick={() => void save()}>
                    {saveLabel}
                  </IonButton>
                )}
              </>
            )}
          </section>
          {saveError && <p role="alert" className="te-quote-error">{saveError}</p>}
          {page && (step === 2 || step === 3) && missingCostFields.length > 0 && <p className="te-quote-error" role="status">Costi da completare: {missingCostFields.join(', ')}. Inserisci 0 solo per i costi effettivamente assenti.</p>}
          {page && <div className="te-quote-actions"><IonButton fill="outline" disabled={step === 0 || saving} onClick={() => goToStep(step - 1)}>Indietro</IonButton>{step < 3 ? <IonButton onClick={() => goToStep(step + 1)}>Continua: {steps[step + 1]}</IonButton> : <IonButton disabled={!canSave} onClick={() => void save()}>{saveLabel}</IonButton>}</div>}
          </div>
          {page && <aside className="te-quote-summary" aria-label="Riepilogo del preventivo"><span>IL TUO EVENTO</span><h2>{eventName.trim() || 'Evento da nominare'}</h2><p>{customerName || 'Cliente da indicare'} · {eventDate ? new Date(`${eventDate}T12:00:00`).toLocaleDateString('it-IT') : 'Data da indicare'}</p><dl><div><dt>Coperti previsti</dt><dd>{guestsExpected || '—'}</dd></div><div><dt>Piatti selezionati</dt><dd>{selectedRecipeIds.length}</dd></div><div><dt>Costo menu / ospite</dt><dd>{money(recipeMenuCost ?? (manualCostConfirmed ? numberOrNull(manualMenuCost) : null))}</dd></div><div><dt>Costo complessivo previsto</dt><dd>{money(expected?.totalCost ?? null)}</dd></div><div><dt>Risultato dopo struttura</dt><dd>{money(expected?.operatingResult ?? null)}</dd></div><div><dt>Prezzo obiettivo lordo / ospite</dt><dd>{money(expected?.targetGrossPricePerGuest ?? null)}</dd></div></dl><p className="te-quote-summary__note">Stima sui costi inseriti, non consuntivo. I valori mancanti restano indicati con —.</p>{!canSave && <p role="status">{!eventName.trim() ? 'Indica il nome dell’evento.' : invalidPortions ? 'Controlla le porzioni dei piatti.' : numberOrNull(forecastAdjustmentPct) == null ? 'Controlla l’adeguamento dei prezzi.' : logisticsMode !== 'onsite' && !destinationId ? 'Scegli la sala di destinazione.' : calculated.error ?? 'Completa o conferma la base di costo del menu.'}</p>}<IonButton expand="block" fill="outline" onClick={() => goToStep(3)}>Controlla il riepilogo</IonButton></aside>}
        </div>
      </IonContent>
    </>
  );
  return page ? <IonPage>{body}</IonPage> : <IonModal isOpen={isOpen} onDidDismiss={onClose}>{body}</IonModal>;
}
